import { useEffect, useState } from "react";
import DashboardShell from "@/components/DashboardShell";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { AlertTriangle, KeyRound } from "lucide-react";
import AdminSendResetButton from "@/components/AdminSendResetButton";
import { formatUsdt, UsdtAmount } from "@/lib/format";

function Chip({ status }) {
  const map = {
    paid: "bg-emerald-100 text-emerald-800", approved: "bg-emerald-100 text-emerald-800", active: "bg-emerald-100 text-emerald-800",
    pending: "bg-amber-100 text-amber-900", verifying: "bg-amber-100 text-amber-900", under_review: "bg-amber-100 text-amber-900",
    submitted: "bg-blue-100 text-blue-800", processing: "bg-blue-100 text-blue-800", open: "bg-blue-100 text-blue-800",
    failed: "bg-red-100 text-red-800", rejected: "bg-red-100 text-red-800", banned: "bg-red-100 text-red-800",
    refunded: "bg-[#1B3455]/70 text-slate-200", reversed: "bg-[#1B3455]/70 text-slate-200", withdrawn: "bg-[#1B3455]/70 text-slate-200",
    closed: "bg-[#1B3455]/70 text-slate-200", not_submitted: "bg-[#1B3455]/70 text-slate-200",
  };
  return <span className={`chip ${map[status] || "bg-[#1B3455]/60 text-slate-200"}`}>{(status || "").replaceAll("_", " ")}</span>;
}

function StatCard({ label, value, sub, testid }) {
  return (
    <div className="crown-card p-5" data-testid={testid}>
      <div className="overline">{label}</div>
      <div className="mt-2 stat-number">{value}</div>
      {sub && <div className="text-xs text-slate-400 mt-1">{sub}</div>}
    </div>
  );
}

export function ForceChangePassword({ onDone }) {
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [cur, setCur] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (pw !== pw2) return toast.error("Passwords do not match");
    setLoading(true);
    try {
      await api.post("/auth/change-password", { current_password: cur, new_password: pw });
      toast.success("Password updated");
      onDone?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#2A331A] flex items-center justify-center p-6">
      <div className="bg-[#132741] rounded-lg p-8 max-w-md w-full">
        <div className="flex items-center gap-3 mb-2">
          <KeyRound className="h-6 w-6 text-[#D4AF37]" />
          <h1 className="font-serif text-2xl">Set your admin password</h1>
        </div>
        <p className="text-sm text-slate-300 mb-6">For security, set a strong password before continuing.</p>
        <form onSubmit={submit} className="space-y-3" data-testid="force-change-form">
          <div>
            <Label>Current password</Label>
            <Input type="password" required value={cur} onChange={(e) => setCur(e.target.value)} data-testid="fc-current" />
          </div>
          <div>
            <Label>New password</Label>
            <Input type="password" required value={pw} onChange={(e) => setPw(e.target.value)} data-testid="fc-new" />
          </div>
          <div>
            <Label>Confirm new password</Label>
            <Input type="password" required value={pw2} onChange={(e) => setPw2(e.target.value)} data-testid="fc-confirm" />
          </div>
          <Button type="submit" disabled={loading} className="w-full bg-[#556B2F] text-white" data-testid="fc-submit">
            {loading ? "Updating..." : "Update password"}
          </Button>
        </form>
      </div>
    </div>
  );
}

export function AdminOverview() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/admin/stats").then(({ data }) => setS(data)); }, []);
  return (
    <DashboardShell isAdmin>
      <div className="mb-8"><div className="overline">Admin</div><h1 className="font-serif text-4xl">Overview</h1></div>
      {!s ? <div className="text-slate-400">Loading...</div> : (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard testid="a-stat-users" label="Total Users" value={s.total_users} />
          <StatCard testid="a-stat-sold" label="Coupons Sold" value={s.coupons_sold.toLocaleString()} />
          <StatCard testid="a-stat-remaining" label="Remaining" value={s.remaining_coupons.toLocaleString()} />
          <StatCard testid="a-stat-sales" label="USDT Sales" value={formatUsdt(s.total_usdt_sales, { withSymbol: false })} sub="USDT" />
          <StatCard testid="a-stat-approved" label="Approved Commissions" value={formatUsdt(s.approved_commission, { withSymbol: false })} sub="USDT" />
          <StatCard testid="a-stat-withdrawn" label="USDT Withdrawn" value={formatUsdt(s.usdt_withdrawn, { withSymbol: false })} sub="USDT" />
          <StatCard testid="a-stat-pending-wd" label="Pending Withdrawals" value={s.pending_withdrawals} />
          <StatCard testid="a-stat-tickets" label="Open Tickets" value={s.open_tickets} />
          <StatCard testid="a-stat-kyc" label="Pending KYC" value={s.pending_kyc} />
        </div>
      )}
    </DashboardShell>
  );
}

export function AdminUsers() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [teamFor, setTeamFor] = useState(null);
  const [teamData, setTeamData] = useState(null);
  const [teamLoading, setTeamLoading] = useState(false);
  const [fundsFor, setFundsFor] = useState(null);
  const [fundsKind, setFundsKind] = useState("credit");
  const [fundsAmount, setFundsAmount] = useState("");
  const [fundsReason, setFundsReason] = useState("");
  const [fundsBusy, setFundsBusy] = useState(false);

  const load = () => api.get(`/admin/users?q=${encodeURIComponent(q)}`).then(({ data }) => setRows(data));
  useEffect(() => { load(); }, []);

  const suspend = async (id) => {
    try { await api.post(`/admin/users/${id}/suspend`); load(); toast.success("Updated"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const openTeam = async (u) => {
    setTeamFor(u); setTeamData(null); setTeamLoading(true);
    try {
      const { data } = await api.get(`/admin/users/${u.id}/team`);
      setTeamData(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setTeamLoading(false); }
  };

  const openFunds = (u) => { setFundsFor(u); setFundsKind("credit"); setFundsAmount(""); setFundsReason(""); };

  const submitFunds = async () => {
    const amt = parseFloat(fundsAmount);
    if (!Number.isFinite(amt) || amt <= 0) return toast.error("Enter a valid amount");
    if (!fundsReason.trim()) return toast.error("Reason is required");
    if (!window.confirm(`Confirm ${fundsKind.toUpperCase()} ${amt.toFixed(2)} USDT for ${fundsFor.email}?`)) return;
    setFundsBusy(true);
    try {
      const { data } = await api.post(`/admin/users/${fundsFor.id}/adjust-funds`, {
        kind: fundsKind, amount: amt, reason: fundsReason.trim(),
      });
      toast.success(`${fundsKind === "credit" ? "Credited" : "Debited"} — ref ${data.reference_id}. New balance ${Number(data.new_balance).toFixed(2)} USDT`);
      setFundsFor(null);
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setFundsBusy(false); }
  };

  return (
    <DashboardShell isAdmin>
      <h1 className="font-serif text-4xl mb-6">Users</h1>
      <div className="flex gap-2 mb-4">
        <Input placeholder="Search by name, email, ID, referral..." value={q} onChange={(e) => setQ(e.target.value)} data-testid="admin-users-search" />
        <Button onClick={load} className="bg-[#556B2F] text-white" data-testid="admin-users-search-btn">Search</Button>
      </div>
      <div className="crown-card p-6">
        <Table>
          <TableHeader><TableRow>
            <TableHead>User ID</TableHead><TableHead>Name</TableHead><TableHead>Email</TableHead>
            <TableHead>Referral</TableHead><TableHead>Direct Refs</TableHead><TableHead>KYC</TableHead>
            <TableHead>Status</TableHead><TableHead>Balance</TableHead><TableHead>Actions</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {rows.map((u) => (
              <TableRow key={u.id} data-testid={`admin-user-row-${u.id}`}>
                <TableCell className="tabular text-xs">{u.id}</TableCell>
                <TableCell>{u.full_name}</TableCell>
                <TableCell className="text-xs">{u.email}</TableCell>
                <TableCell className="tabular text-xs">{u.referral_id}</TableCell>
                <TableCell className="tabular text-center" data-testid={`u-direct-${u.id}`}>{u.direct_referrals_count ?? 0}</TableCell>
                <TableCell><Chip status={u.kyc_status} /></TableCell>
                <TableCell><Chip status={u.status} /></TableCell>
                <TableCell className="tabular text-xs" data-testid={`u-balance-${u.id}`}>
                  {u.role === "admin" ? "—" : <UsdtAmount value={u.available_balance ?? 0} decimals={2} />}
                </TableCell>
                <TableCell>
                  {u.role !== "admin" && (
                    <div className="flex flex-wrap gap-1">
                      <Button size="sm" variant="outline" onClick={() => openTeam(u)} data-testid={`view-team-${u.id}`}>View Team</Button>
                      <Button size="sm" variant="outline" onClick={() => openFunds(u)} data-testid={`manage-funds-${u.id}`}>Manage Funds</Button>
                      <Button size="sm" variant="outline" onClick={() => suspend(u.id)} data-testid={`suspend-${u.id}`}>
                        {u.status === "banned" ? "Reactivate" : "Suspend"}
                      </Button>
                    </div>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* View Team dialog */}
      <Dialog open={!!teamFor} onOpenChange={(v) => !v && setTeamFor(null)}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Team of {teamFor?.full_name || teamFor?.email}</DialogTitle></DialogHeader>
          {teamLoading && <div className="text-sm text-slate-400 py-6">Loading…</div>}
          {teamData && <TeamView data={teamData} />}
        </DialogContent>
      </Dialog>

      {/* Manage Funds dialog */}
      <Dialog open={!!fundsFor} onOpenChange={(v) => !v && setFundsFor(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Manage Funds — {fundsFor?.full_name || fundsFor?.email}</DialogTitle></DialogHeader>
          {fundsFor && (
            <div className="space-y-3 text-sm">
              <div>Current available: <span className="tabular font-medium"><UsdtAmount value={fundsFor.available_balance || 0} decimals={2} /></span></div>
              <div className="flex gap-2">
                <button type="button" onClick={() => setFundsKind("credit")} data-testid="funds-kind-credit" className={`flex-1 px-3 py-2 rounded-md text-sm ${fundsKind === "credit" ? "bg-emerald-700 text-white" : "border border-white/10 text-slate-300"}`}>Credit (Add)</button>
                <button type="button" onClick={() => setFundsKind("debit")} data-testid="funds-kind-debit" className={`flex-1 px-3 py-2 rounded-md text-sm ${fundsKind === "debit" ? "bg-red-700 text-white" : "border border-white/10 text-slate-300"}`}>Debit (Deduct)</button>
              </div>
              <div>
                <Label>Amount (USDT)</Label>
                <Input type="number" min={0} step="0.01" value={fundsAmount} onChange={(e) => setFundsAmount(e.target.value)} data-testid="funds-amount" />
              </div>
              <div>
                <Label>Reason</Label>
                <Input value={fundsReason} onChange={(e) => setFundsReason(e.target.value)} placeholder="e.g., promotion payout / correction / test refund" data-testid="funds-reason" />
              </div>
              <div className="text-[10px] text-slate-400">A reference ID + audit log entry will be created.</div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setFundsFor(null)}>Cancel</Button>
            <Button onClick={submitFunds} disabled={fundsBusy} className={fundsKind === "credit" ? "bg-emerald-700 text-white" : "bg-red-700 text-white"} data-testid="funds-confirm">{fundsBusy ? "Saving…" : `Confirm ${fundsKind}`}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardShell>
  );
}

function TeamView({ data }) {
  const lc = data.level_counts || {};
  const tree = data.tree || {};
  return (
    <div className="space-y-4" data-testid="team-view">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <div className="crown-card p-3"><div className="overline">Direct</div><div className="stat-number text-xl" data-testid="team-direct">{data.direct_referrals_count}</div></div>
        <div className="crown-card p-3"><div className="overline">Total Downline</div><div className="stat-number text-xl" data-testid="team-total">{data.total_downline}</div></div>
        <div className="crown-card p-3"><div className="overline">Active</div><div className="stat-number text-xl text-emerald-400" data-testid="team-active">{data.active_members}</div></div>
        <div className="crown-card p-3"><div className="overline">Inactive</div><div className="stat-number text-xl text-slate-400" data-testid="team-inactive">{data.inactive_members}</div></div>
      </div>
      <div className="grid grid-cols-3 gap-2 text-center text-xs">
        <div className="rounded-md border border-white/10 p-2">L1<br/><span className="stat-number text-lg" data-testid="team-l1">{lc["1"] ?? 0}</span></div>
        <div className="rounded-md border border-white/10 p-2">L2<br/><span className="stat-number text-lg" data-testid="team-l2">{lc["2"] ?? 0}</span></div>
        <div className="rounded-md border border-white/10 p-2">L3<br/><span className="stat-number text-lg" data-testid="team-l3">{lc["3"] ?? 0}</span></div>
      </div>
      {["1","2","3"].map((lv) => (
        (tree[lv] || []).length > 0 && (
          <div key={lv}>
            <div className="overline mb-1">Level {lv}</div>
            <div className="rounded-md border border-white/10 divide-y divide-white/5">
              {(tree[lv] || []).map((m) => (
                <div key={m.id} className="p-2 text-xs flex items-center justify-between" data-testid={`team-node-${m.id}`}>
                  <div>
                    <div className="font-medium">{m.full_name || m.email}</div>
                    <div className="text-slate-400">{m.email} · {m.id}</div>
                  </div>
                  <div className="text-right text-[10px]">
                    <Chip status={m.status || "active"} />
                    <div className="text-slate-500 mt-1">{m.created_at ? new Date(m.created_at).toLocaleDateString() : ""}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )
      ))}
    </div>
  );
}

export function AdminOrders() {
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("");
  const load = () => api.get(`/admin/orders?status=${status}`).then(({ data }) => setRows(data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);
  const decide = async (id, action, reason = "") => {
    try { await api.post(`/admin/orders/${id}/decision`, { action, reason }); toast.success("Done"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <DashboardShell isAdmin>
      <h1 className="font-serif text-4xl mb-6">Orders</h1>
      <div className="mb-4 flex gap-2">
        {["", "pending", "verifying", "paid", "failed", "refunded"].map((s) => (
          <button key={s || "all"} onClick={() => setStatus(s)} className={`chip ${status === s ? "bg-[#556B2F] text-white" : "bg-[#1B3455]/60 text-slate-200"}`} data-testid={`filter-${s || "all"}`}>{s || "all"}</button>
        ))}
      </div>
      <div className="crown-card p-6">
        <Table>
          <TableHeader><TableRow><TableHead>Order</TableHead><TableHead>User</TableHead><TableHead>Amount</TableHead><TableHead>Tx</TableHead><TableHead>Status</TableHead><TableHead>Actions</TableHead></TableRow></TableHeader>
          <TableBody>
            {rows.map((o) => (
              <TableRow key={o.id} data-testid={`admin-order-${o.id}`}>
                <TableCell className="tabular text-xs">{o.id}</TableCell>
                <TableCell className="text-xs">{o.user_email}</TableCell>
                <TableCell className="tabular"><UsdtAmount value={o.amount_usdt} decimals={0} /></TableCell>
                <TableCell className="text-xs tabular truncate max-w-[140px]">{o.tx_hash || "—"}</TableCell>
                <TableCell><Chip status={o.status} /></TableCell>
                <TableCell className="flex gap-2">
                  {o.status === "verifying" && <Button size="sm" onClick={() => decide(o.id, "verify")} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`verify-${o.id}`}>Verify</Button>}
                  {["verifying", "pending"].includes(o.status) && <Button size="sm" variant="outline" onClick={() => decide(o.id, "fail", "Rejected by admin")} data-testid={`fail-${o.id}`}>Fail</Button>}
                  {o.status === "paid" && <Button size="sm" variant="outline" onClick={() => decide(o.id, "refund", "Refunded by admin")} data-testid={`refund-${o.id}`}>Refund</Button>}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </DashboardShell>
  );
}

export function AdminCommissions() {
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("pending");
  const load = () => api.get(`/admin/commissions?status=${status}`).then(({ data }) => setRows(data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);
  const approve = async (id) => { await api.post(`/admin/commissions/${id}/approve`); toast.success("Approved"); load(); };
  const approveAll = async () => { const r = await api.post("/admin/commissions/approve-all-pending"); toast.success(`Approved ${r.data.count}`); load(); };
  return (
    <DashboardShell isAdmin>
      <h1 className="font-serif text-4xl mb-6">Commissions</h1>
      <div className="mb-4 flex gap-2">
        {["pending", "approved", "withdrawn", "reversed"].map((s) => (
          <button key={s} onClick={() => setStatus(s)} className={`chip ${status === s ? "bg-[#556B2F] text-white" : "bg-[#1B3455]/60 text-slate-200"}`} data-testid={`comm-filter-${s}`}>{s}</button>
        ))}
        {status === "pending" && <Button onClick={approveAll} className="ml-auto bg-emerald-600 text-white" data-testid="approve-all">Approve all pending</Button>}
      </div>
      <div className="crown-card p-6">
        <Table>
          <TableHeader><TableRow><TableHead>ID</TableHead><TableHead>User</TableHead><TableHead>Order</TableHead><TableHead>Lv</TableHead><TableHead>Sale</TableHead><TableHead>Amount</TableHead><TableHead>Status</TableHead><TableHead>Actions</TableHead></TableRow></TableHeader>
          <TableBody>
            {rows.map((c) => (
              <TableRow key={c.id}>
                <TableCell className="tabular text-xs">{c.id}</TableCell>
                <TableCell className="tabular text-xs">{c.user_id}</TableCell>
                <TableCell className="tabular text-xs">{c.source_order_id}</TableCell>
                <TableCell>L{c.level}</TableCell>
                <TableCell className="tabular"><UsdtAmount value={c.sale_amount} decimals={0} /></TableCell>
                <TableCell className="tabular font-medium"><UsdtAmount value={c.commission_amount} /></TableCell>
                <TableCell><Chip status={c.status} /></TableCell>
                <TableCell>{c.status === "pending" && <Button size="sm" onClick={() => approve(c.id)} className="bg-emerald-600 text-white" data-testid={`approve-${c.id}`}>Approve</Button>}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </DashboardShell>
  );
}

export function AdminWithdrawals() {
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("");
  const [decision, setDecision] = useState({ open: false, id: null, status: "paid", tx_hash: "", reason: "" });
  const load = () => api.get(`/admin/withdrawals?status=${status}`).then(({ data }) => setRows(data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);
  const save = async () => {
    try {
      await api.post(`/admin/withdrawals/${decision.id}/decision`, { status: decision.status, tx_hash: decision.tx_hash, reason: decision.reason });
      toast.success("Updated");
      setDecision({ ...decision, open: false });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <DashboardShell isAdmin>
      <h1 className="font-serif text-4xl mb-6">Withdrawals</h1>
      <div className="mb-4 flex gap-2">
        {["", "pending", "under_review", "approved", "processing", "paid", "rejected"].map((s) => (
          <button key={s || "all"} onClick={() => setStatus(s)} className={`chip ${status === s ? "bg-[#556B2F] text-white" : "bg-[#1B3455]/60 text-slate-200"}`} data-testid={`wd-filter-${s || "all"}`}>{s || "all"}</button>
        ))}
      </div>
      <div className="crown-card p-6">
        <Table>
          <TableHeader><TableRow><TableHead>ID</TableHead><TableHead>User</TableHead><TableHead>Amount</TableHead><TableHead>Wallet</TableHead><TableHead>Tx</TableHead><TableHead>Status</TableHead><TableHead>Actions</TableHead></TableRow></TableHeader>
          <TableBody>
            {rows.map((w) => (
              <TableRow key={w.id}>
                <TableCell className="tabular text-xs">{w.id}</TableCell>
                <TableCell className="text-xs">{w.user_email}</TableCell>
                <TableCell className="tabular"><UsdtAmount value={w.amount} /></TableCell>
                <TableCell className="text-xs tabular truncate max-w-[140px]">{w.wallet_address}</TableCell>
                <TableCell className="text-xs tabular truncate max-w-[140px]">{w.tx_hash || "—"}</TableCell>
                <TableCell><Chip status={w.status} /></TableCell>
                <TableCell>
                  <Button size="sm" onClick={() => setDecision({ open: true, id: w.id, status: "paid", tx_hash: "", reason: "" })} data-testid={`wd-decide-${w.id}`}>Decide</Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <Dialog open={decision.open} onOpenChange={(o) => setDecision({ ...decision, open: o })}>
        <DialogContent>
          <DialogHeader><DialogTitle>Withdrawal decision</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Label>Status</Label>
            <select value={decision.status} onChange={(e) => setDecision({ ...decision, status: e.target.value })} className="w-full h-10 border border-input rounded-md px-3 text-sm" data-testid="wd-decision-status">
              {["under_review", "approved", "processing", "paid", "rejected"].map((s) => <option key={s}>{s}</option>)}
            </select>
            {decision.status === "paid" && <Input placeholder="Tx hash" value={decision.tx_hash} onChange={(e) => setDecision({ ...decision, tx_hash: e.target.value })} data-testid="wd-decision-tx" />}
            {decision.status === "rejected" && <Input placeholder="Reason" value={decision.reason} onChange={(e) => setDecision({ ...decision, reason: e.target.value })} data-testid="wd-decision-reason" />}
          </div>
          <DialogFooter>
            <Button onClick={save} className="bg-[#556B2F] text-white" data-testid="wd-decision-save">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardShell>
  );
}

export function AdminKyc() {
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("pending");
  const load = () => api.get(`/admin/kyc?status=${status}`).then(({ data }) => setRows(data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);
  const decide = async (id, s, reason = "") => {
    await api.post(`/admin/kyc/${id}/decision`, { status: s, reason });
    toast.success("Done"); load();
  };
  const viewFile = async (path) => {
    const { data } = await api.get(`/kyc/file?path=${encodeURIComponent(path)}`, { responseType: "blob" });
    const url = URL.createObjectURL(data);
    window.open(url, "_blank");
  };
  return (
    <DashboardShell isAdmin>
      <h1 className="font-serif text-4xl mb-6">KYC</h1>
      <div className="mb-4 flex gap-2">
        {["pending", "approved", "rejected"].map((s) => (
          <button key={s} onClick={() => setStatus(s)} className={`chip ${status === s ? "bg-[#556B2F] text-white" : "bg-[#1B3455]/60 text-slate-200"}`} data-testid={`kyc-filter-${s}`}>{s}</button>
        ))}
      </div>
      <div className="crown-card p-6">
        <Table>
          <TableHeader><TableRow><TableHead>User</TableHead><TableHead>Doc Type</TableHead><TableHead>DOB</TableHead><TableHead>Files</TableHead><TableHead>Status</TableHead><TableHead>Actions</TableHead></TableRow></TableHeader>
          <TableBody>
            {rows.map((k) => (
              <TableRow key={k.id}>
                <TableCell className="text-xs">{k.user_email}</TableCell>
                <TableCell>{k.doc_type}</TableCell>
                <TableCell className="text-xs">{k.dob}</TableCell>
                <TableCell>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => viewFile(k.id_file.path)} data-testid={`kyc-view-id-${k.id}`}>ID</Button>
                    <Button size="sm" variant="outline" onClick={() => viewFile(k.address_file.path)} data-testid={`kyc-view-addr-${k.id}`}>Address</Button>
                  </div>
                </TableCell>
                <TableCell><Chip status={k.status} /></TableCell>
                <TableCell className="flex gap-2">
                  {k.status === "pending" && <>
                    <Button size="sm" onClick={() => decide(k.id, "approved")} className="bg-emerald-600 text-white" data-testid={`kyc-approve-${k.id}`}>Approve</Button>
                    <Button size="sm" variant="outline" onClick={() => decide(k.id, "rejected", "Documents unclear")} data-testid={`kyc-reject-${k.id}`}>Reject</Button>
                  </>}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </DashboardShell>
  );
}

export function AdminTickets() {
  const [rows, setRows] = useState([]);
  const [selected, setSelected] = useState(null);
  const [reply, setReply] = useState("");
  const [internal, setInternal] = useState(false);
  const load = () => api.get("/admin/tickets").then(({ data }) => setRows(data));
  useEffect(() => { load(); }, []);
  const open = async (id) => { const { data } = await api.get(`/support/tickets/${id}`); setSelected(data); };
  const send = async () => {
    if (!reply.trim()) return;
    await api.post(`/support/tickets/${selected.id}/reply`, { message: reply, internal });
    setReply(""); open(selected.id); load();
  };
  const close = async () => { await api.post(`/admin/tickets/${selected.id}/close`); load(); open(selected.id); };
  return (
    <DashboardShell isAdmin>
      <h1 className="font-serif text-4xl mb-6">Support Tickets</h1>
      <div className="grid lg:grid-cols-3 gap-6">
        <div className="crown-card p-4 lg:col-span-1 max-h-[70vh] overflow-auto">
          {rows.map((t) => (
            <button key={t.id} onClick={() => open(t.id)} className={`w-full text-left p-3 rounded-md border mb-2 ${selected?.id === t.id ? "border-[#556B2F] bg-[#556B2F]/5" : "border-slate-200"}`} data-testid={`admin-ticket-${t.id}`}>
              <div className="font-medium">{t.subject}</div>
              <div className="text-xs text-slate-400">{t.user_email} · <Chip status={t.status} /></div>
            </button>
          ))}
        </div>
        <div className="crown-card p-6 lg:col-span-2">
          {!selected ? <div className="text-slate-400">Select a ticket</div> : (
            <>
              <div className="flex justify-between items-start mb-3">
                <div><div className="font-serif text-2xl">{selected.subject}</div><div className="text-xs text-slate-400">{selected.id} · {selected.category}</div></div>
                {selected.status === "open" && <Button variant="outline" onClick={close} data-testid="admin-ticket-close">Close</Button>}
              </div>
              <div className="space-y-2 max-h-[45vh] overflow-auto">
                {selected.messages.map((m, i) => (
                  <div key={`${m.at}-${m.author}-${i}`} className={`p-3 rounded ${m.from === "admin" ? "bg-[#556B2F]/5" : "bg-[#1B3455]/40"} ${m.internal ? "border-2 border-amber-300" : ""}`}>
                    <div className="text-xs text-slate-400 mb-1">{m.author} {m.internal && "· INTERNAL"} · {new Date(m.at).toLocaleString()}</div>
                    <div className="text-sm">{m.message}</div>
                  </div>
                ))}
              </div>
              {selected.status !== "closed" && (
                <div className="mt-4 flex gap-2 items-center">
                  <Input value={reply} onChange={(e) => setReply(e.target.value)} placeholder="Reply..." data-testid="admin-ticket-reply" />
                  <label className="text-xs flex items-center gap-1"><input type="checkbox" checked={internal} onChange={(e) => setInternal(e.target.checked)} data-testid="admin-ticket-internal" /> Internal</label>
                  <Button onClick={send} className="bg-[#556B2F] text-white" data-testid="admin-ticket-send">Send</Button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </DashboardShell>
  );
}

export function AdminSettings() {
  const [s, setS] = useState(null);
  const [coupon, setCoupon] = useState({ total_inventory: 10000000, price_usdt: 1000, active: true });
  const [comm, setComm] = useState({ level1: 5, level2: 3, level3: 2 });
  const [wallet, setWallet] = useState({ admin_password: "", new_address: "", confirm_address: "" });
  const [payment, setPayment] = useState({
    test_mode: true, chain_id: 56, rpc_url: "https://bsc-dataseed.binance.org/",
    explorer_url: "https://bscscan.com", usdt_contract: "0x55d398326f99059fF775485246999027B3197955",
    usdt_decimals: 18, required_confirmations: 3, order_expiration_minutes: 30,
  });
  const [kycGate, setKycGate] = useState({ required_for_purchase: false, required_for_referral_rewards: false, required_for_withdrawal: true, required_for_boombox_withdrawal: false });
  const [activity, setActivity] = useState({ value: 287879, next_update_days: 22 });

  useEffect(() => {
    api.get("/admin/settings").then(({ data }) => {
      setS(data); setCoupon(data.coupon); setComm(data.commissions);
      if (data.payment) setPayment(data.payment);
      if (data.kyc_gate) setKycGate(data.kyc_gate);
      if (data.activity) setActivity({
        value: data.activity.value ?? 287879,
        next_update_days: data.activity.next_update_days ?? 22,
      });
    });
  }, []);

  const saveCoupon = async () => {
    try { await api.post("/admin/settings/coupon", coupon); toast.success("Saved"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const saveComm = async () => {
    try { await api.post("/admin/settings/commissions", comm); toast.success("Saved"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const saveWallet = async () => {
    try {
      const payload = {
        admin_password: wallet.admin_password,
        new_address: (wallet.new_address || "").trim(),
        confirm_address: (wallet.confirm_address || "").trim(),
      };
      // Client-side pre-validation with clear messages
      if (!payload.admin_password) return toast.error("Please enter your admin password to confirm this change");
      if (!payload.new_address || !payload.confirm_address) return toast.error("Please enter and confirm the new receiving wallet address");
      if (payload.new_address.toLowerCase() !== payload.confirm_address.toLowerCase()) return toast.error("New address and confirmation address do not match");
      if (!payload.new_address.startsWith("0x") || payload.new_address.length !== 42) return toast.error("Invalid BSC address — must start with 0x and be exactly 42 characters");
      if (!/^[0-9a-fA-F]{40}$/.test(payload.new_address.slice(2))) return toast.error("Invalid BSC address — contains non-hex characters");

      const { data } = await api.post("/admin/settings/wallet", payload);
      toast.success(data?.message || "Receiving wallet updated");
      setWallet({ admin_password: "", new_address: "", confirm_address: "" });
      const { data: fresh } = await api.get("/admin/settings");
      setS(fresh);
    } catch (e) {
      const status = e.response?.status;
      const detail = e.response?.data?.detail;
      const specific = formatApiError(detail, "");
      if (status === 401 && !specific) toast.error("Your admin session has expired — please sign in again");
      else if (status === 401) toast.error(specific);
      else if (status === 400) toast.error(specific || "The request was rejected — please check the address and try again");
      else if (status === 403) toast.error("Forbidden — you do not have permission to change the wallet");
      else if (!e.response) toast.error("Network error — could not reach the server");
      else toast.error(specific || `Update failed (HTTP ${status || "unknown"})`);
    }
  };
  const savePayment = async () => {
    try { await api.post("/admin/settings/payment", payment); toast.success("Payment settings saved"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const saveKycGate = async () => {
    try { await api.post("/admin/settings/kyc-gate", kycGate); toast.success("KYC policy saved"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const saveActivity = async () => {
    const value = parseInt(activity.value);
    const days = parseInt(activity.next_update_days);
    if (!Number.isFinite(value) || value < 0) return toast.error("Activity count must be a non-negative whole number");
    if (!Number.isFinite(days) || days < 0) return toast.error("Next update days must be a non-negative whole number");
    try {
      await api.post("/admin/settings/activity", { value, next_update_days: days });
      toast.success("Activity count updated");
      const { data: fresh } = await api.get("/admin/settings");
      setS(fresh);
      if (fresh.activity) setActivity({ value: fresh.activity.value, next_update_days: fresh.activity.next_update_days });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <DashboardShell isAdmin>
      <h1 className="font-serif text-4xl mb-6">Settings</h1>
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="crown-card p-6">
          <div className="overline mb-3">Coupon inventory</div>
          <div className="space-y-3">
            <div><Label>Total inventory</Label><Input type="number" value={coupon.total_inventory} onChange={(e) => setCoupon({ ...coupon, total_inventory: parseInt(e.target.value || 0) })} data-testid="set-inventory" /></div>
            <div><Label>Price (USDT)</Label><Input type="number" step="0.01" value={coupon.price_usdt} onChange={(e) => setCoupon({ ...coupon, price_usdt: parseFloat(e.target.value || 0) })} data-testid="set-price" /></div>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={coupon.active} onChange={(e) => setCoupon({ ...coupon, active: e.target.checked })} data-testid="set-active" /> Sales active</label>
            <Button onClick={saveCoupon} className="bg-[#556B2F] text-white" data-testid="save-coupon">Save</Button>
          </div>
        </div>

        <div className="crown-card p-6">
          <div className="overline mb-3">Commission rates (%)</div>
          <div className="grid grid-cols-3 gap-3">
            <div><Label>Level 1</Label><Input type="number" step="0.1" value={comm.level1} onChange={(e) => setComm({ ...comm, level1: parseFloat(e.target.value || 0) })} data-testid="set-l1" /></div>
            <div><Label>Level 2</Label><Input type="number" step="0.1" value={comm.level2} onChange={(e) => setComm({ ...comm, level2: parseFloat(e.target.value || 0) })} data-testid="set-l2" /></div>
            <div><Label>Level 3</Label><Input type="number" step="0.1" value={comm.level3} onChange={(e) => setComm({ ...comm, level3: parseFloat(e.target.value || 0) })} data-testid="set-l3" /></div>
          </div>
          <div className="text-xs text-slate-400 mt-3">Referral rewards are paid only on genuine, verified qualifying sales. Not on sign-ups, deposits, or wallet connections.</div>
          <Button onClick={saveComm} className="bg-[#556B2F] text-white mt-4" data-testid="save-comm">Save</Button>
        </div>

        <div className="crown-card p-6 lg:col-span-2 border-l-4 border-[#3B4A1F]">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle className="h-5 w-5 text-[#D4AF37]" />
            <div className="overline">Receiving wallet (BSC)</div>
          </div>
          <div className="text-sm text-slate-300 mb-4">Current: <span className="font-mono">{s?.receiving_wallet || "Not set"}</span></div>
          <div className="grid md:grid-cols-3 gap-3">
            <div><Label>New address</Label><Input value={wallet.new_address} onChange={(e) => setWallet({ ...wallet, new_address: e.target.value })} placeholder="0x..." data-testid="wallet-new" /></div>
            <div><Label>Confirm address</Label><Input value={wallet.confirm_address} onChange={(e) => setWallet({ ...wallet, confirm_address: e.target.value })} placeholder="0x..." data-testid="wallet-confirm" /></div>
            <div><Label>Admin password</Label><Input type="password" value={wallet.admin_password} onChange={(e) => setWallet({ ...wallet, admin_password: e.target.value })} data-testid="wallet-pw" /></div>
          </div>
          <div className="mt-4 rounded-md bg-red-50 border border-red-200 p-3 text-xs text-red-900">Warning: This address will receive all customer USDT payments. Never store your private key or seed here — only the public receiving address.</div>
          <Button onClick={saveWallet} className="mt-4 bg-[#3B4A1F] text-white" data-testid="save-wallet">Update receiving wallet</Button>
        </div>

        <div className="crown-card p-6 lg:col-span-2">
          <div className="flex items-center justify-between mb-3">
            <div><div className="overline">Payment settings (BSC)</div><div className="font-serif text-2xl">On-chain verification</div></div>
            <label className="flex items-center gap-2 text-sm px-3 py-1.5 rounded-full bg-amber-50 border border-amber-200 text-amber-900" data-testid="test-mode-label">
              <input type="checkbox" checked={payment.test_mode} onChange={(e) => setPayment({ ...payment, test_mode: e.target.checked })} data-testid="set-test-mode" />
              Test mode {payment.test_mode ? "ON" : "OFF"}
            </label>
          </div>
          <div className="text-xs text-slate-300 mb-4">Test mode disables real on-chain verification and requires admin manual approval. Turn OFF before accepting real USDT payments.</div>

          <div className="grid md:grid-cols-2 gap-3">
            <div><Label>Chain ID</Label>
              <select value={payment.chain_id} onChange={(e) => setPayment({ ...payment, chain_id: parseInt(e.target.value) })} className="mt-1 w-full h-10 rounded-md border border-input px-3 text-sm" data-testid="set-chain">
                <option value={56}>56 · BNB Smart Chain (mainnet)</option>
                <option value={97}>97 · BSC Testnet</option>
              </select>
            </div>
            <div><Label>USDT decimals</Label>
              <select value={payment.usdt_decimals} onChange={(e) => setPayment({ ...payment, usdt_decimals: parseInt(e.target.value) })} className="mt-1 w-full h-10 rounded-md border border-input px-3 text-sm" data-testid="set-decimals">
                <option value={18}>18 (BSC USDT)</option>
                <option value={6}>6 (Ethereum-style)</option>
              </select>
            </div>
            <div className="md:col-span-2"><Label>USDT contract address</Label><Input value={payment.usdt_contract} onChange={(e) => setPayment({ ...payment, usdt_contract: e.target.value })} data-testid="set-contract" /></div>
            <div><Label>RPC URL</Label><Input value={payment.rpc_url} onChange={(e) => setPayment({ ...payment, rpc_url: e.target.value })} data-testid="set-rpc" /></div>
            <div><Label>Explorer URL</Label><Input value={payment.explorer_url} onChange={(e) => setPayment({ ...payment, explorer_url: e.target.value })} data-testid="set-explorer" /></div>
            <div><Label>Required confirmations</Label><Input type="number" min={1} max={50} value={payment.required_confirmations} onChange={(e) => setPayment({ ...payment, required_confirmations: parseInt(e.target.value || 3) })} data-testid="set-confirmations" /></div>
            <div><Label>Order expiration (minutes)</Label><Input type="number" min={5} max={240} value={payment.order_expiration_minutes} onChange={(e) => setPayment({ ...payment, order_expiration_minutes: parseInt(e.target.value || 30) })} data-testid="set-expiration" /></div>
          </div>
          <Button onClick={savePayment} className="mt-4 bg-[#556B2F] text-white" data-testid="save-payment">Save payment settings</Button>
        </div>

        <div className="crown-card p-6 lg:col-span-2 border-l-4 border-[#556B2F]">
          <div className="flex items-center gap-2 mb-3">
            <KeyRound className="h-5 w-5 text-[#556B2F]" />
            <div className="overline">Admin security</div>
          </div>
          <div className="text-sm text-slate-300 mb-3">
            Send a one-time password reset link to your verified admin email. The link expires in 60 minutes and can be used once. This never exposes your current password.
          </div>
          <AdminSendResetButton />
          <div className="text-xs text-slate-400 mt-3">
            Any previous unused reset tokens will be invalidated automatically when you request a new link.
          </div>
        </div>

        <div className="crown-card p-6 lg:col-span-2">
          <div className="overline mb-3">KYC policy</div>
          <div className="grid md:grid-cols-3 gap-3 text-sm">
            <label className="flex items-center gap-2"><input type="checkbox" checked={kycGate.required_for_purchase} onChange={(e) => setKycGate({ ...kycGate, required_for_purchase: e.target.checked })} data-testid="kyc-gate-purchase" /> Required before purchase</label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={kycGate.required_for_referral_rewards} onChange={(e) => setKycGate({ ...kycGate, required_for_referral_rewards: e.target.checked })} data-testid="kyc-gate-referral" /> Required for referral rewards</label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={kycGate.required_for_withdrawal} onChange={(e) => setKycGate({ ...kycGate, required_for_withdrawal: e.target.checked })} data-testid="kyc-gate-withdrawal" /> Required for withdrawal</label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={!!kycGate.required_for_boombox_withdrawal} onChange={(e) => setKycGate({ ...kycGate, required_for_boombox_withdrawal: e.target.checked })} data-testid="kyc-gate-boombox-withdrawal" /> Required for Boom Box withdrawal</label>
          </div>
          <div className="text-xs text-slate-400 mt-3">KYC status alone is not a legal sufficiency claim. Apply local regulatory requirements as appropriate.</div>
          <Button onClick={saveKycGate} className="mt-4 bg-[#556B2F] text-white" data-testid="save-kyc-gate">Save KYC policy</Button>
        </div>

        <div className="crown-card p-6 lg:col-span-2 border-l-4 border-[#D4AF37]/60">
          <div className="overline mb-2">Activity count (display-only)</div>
          <div className="text-sm text-slate-300 mb-4">
            A vanity metric shown on the user dashboard. This number is <strong>not linked</strong> to any financial, order, coupon, commission or referral logic — changing it only affects what users see on their dashboard header.
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <Label>Activity count</Label>
              <Input
                type="number"
                min={0}
                step={1}
                value={activity.value}
                onChange={(e) => setActivity({ ...activity, value: e.target.value })}
                data-testid="set-activity-value"
              />
            </div>
            <div>
              <Label>Next update (days)</Label>
              <Input
                type="number"
                min={0}
                step={1}
                value={activity.next_update_days}
                onChange={(e) => setActivity({ ...activity, next_update_days: e.target.value })}
                data-testid="set-activity-days"
              />
            </div>
          </div>
          <div className="text-xs text-slate-400 mt-3">
            Current: <span className="tabular font-medium">{Number(s?.activity?.value ?? 0).toLocaleString()}</span>
            {" · "}
            Next update in <span className="tabular font-medium">{Number(s?.activity?.next_update_days ?? 0)} days</span>
          </div>
          <Button onClick={saveActivity} className="mt-4 bg-[#556B2F] text-white" data-testid="save-activity">Save activity count</Button>
        </div>
      </div>
    </DashboardShell>
  );
}

export function AdminAudit() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/admin/audit-logs").then(({ data }) => setRows(data)); }, []);
  return (
    <DashboardShell isAdmin>
      <h1 className="font-serif text-4xl mb-6">Audit Logs</h1>
      <div className="crown-card p-6">
        <Table>
          <TableHeader><TableRow><TableHead>Date</TableHead><TableHead>Actor</TableHead><TableHead>Action</TableHead><TableHead>Target</TableHead><TableHead>Meta</TableHead></TableRow></TableHeader>
          <TableBody>
            {rows.map((a) => (
              <TableRow key={a.id}>
                <TableCell className="text-xs">{new Date(a.created_at).toLocaleString()}</TableCell>
                <TableCell className="tabular text-xs">{a.actor_id}</TableCell>
                <TableCell>{a.action}</TableCell>
                <TableCell className="tabular text-xs">{a.target}</TableCell>
                <TableCell className="text-xs text-slate-400">{JSON.stringify(a.meta)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </DashboardShell>
  );
}


// =============================================================
//  ADMIN · Boom Box Management
// =============================================================
export function AdminBoomBox() {
  const [subs, setSubs] = useState([]);
  const [withdrawals, setWithdrawals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creditFor, setCreditFor] = useState(null); // subscriber row being credited
  const [creditAmount, setCreditAmount] = useState("");
  const [creditMemo, setCreditMemo] = useState("");
  const [txInputs, setTxInputs] = useState({}); // { wid: tx_hash }

  const load = async () => {
    setLoading(true);
    try {
      const [a, b] = await Promise.all([
        api.get("/admin/boombox/subscribers"),
        api.get("/admin/boombox/withdrawals"),
      ]);
      setSubs(a.data || []);
      setWithdrawals(b.data || []);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const doVerify = async (sid) => {
    try { await api.post(`/admin/boombox/subscriptions/${sid}/verify`); toast.success("Subscription activated"); await load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const doCredit = async () => {
    const amt = parseFloat(creditAmount);
    if (!Number.isFinite(amt) || amt <= 0) return toast.error("Enter a valid amount");
    try {
      await api.post("/admin/boombox/credit", { user_id: creditFor.user_id, amount: amt, memo: creditMemo });
      toast.success("Balance credited");
      setCreditFor(null); setCreditAmount(""); setCreditMemo("");
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const decide = async (wid, status, tx_hash) => {
    try {
      await api.post(`/admin/boombox/withdrawals/${wid}/decision`, { status, tx_hash: tx_hash || null });
      toast.success(`Withdrawal ${status}`);
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <DashboardShell isAdmin>
      <div className="mb-6">
        <div className="overline">Admin</div>
        <h1 className="font-serif text-4xl mt-2">Boom Box Management</h1>
        <p className="text-slate-400 text-sm mt-1">Subscribers, balance credits, and withdrawal approvals.</p>
      </div>

      {/* Subscribers */}
      <div className="crown-card p-6 mb-6" data-testid="admin-bb-subscribers">
        <div className="flex items-center justify-between mb-3">
          <div className="overline">Subscribers</div>
          <Button variant="outline" size="sm" onClick={load} disabled={loading}>Refresh</Button>
        </div>
        <Table>
          <TableHeader><TableRow>
            <TableHead>User</TableHead><TableHead>Status</TableHead><TableHead>Start</TableHead><TableHead>Expiry</TableHead>
            <TableHead>Tx</TableHead><TableHead>Balance</TableHead><TableHead>Actions</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {subs.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-slate-400 py-8">No subscribers yet</TableCell></TableRow>}
            {subs.map((s) => (
              <TableRow key={s.id} data-testid={`bb-sub-${s.id}`}>
                <TableCell className="text-xs">{s.user_email}<div className="text-slate-500">{s.user_id}</div></TableCell>
                <TableCell><Chip status={s.status} /></TableCell>
                <TableCell className="text-xs">{s.started_at ? new Date(s.started_at).toLocaleDateString() : "—"}</TableCell>
                <TableCell className="text-xs">{s.expires_at ? new Date(s.expires_at).toLocaleDateString() : "—"}</TableCell>
                <TableCell className="text-[10px] tabular truncate max-w-[140px]">{s.tx_hash || "—"}</TableCell>
                <TableCell className="text-xs tabular">
                  Avail: <UsdtAmount value={s.balance?.available_balance || 0} decimals={2} /><br/>
                  Hold: <UsdtAmount value={s.balance?.hold_amount || 0} decimals={2} /><br/>
                  Credited: <UsdtAmount value={s.balance?.total_credited || 0} decimals={2} />
                </TableCell>
                <TableCell className="text-xs space-y-1">
                  {s.status === "verifying" && (
                    <Button size="sm" onClick={() => doVerify(s.id)} className="bg-[#556B2F] text-white" data-testid={`btn-verify-${s.id}`}>Verify</Button>
                  )}
                  <Button size="sm" variant="outline" onClick={() => { setCreditFor(s); setCreditAmount(""); setCreditMemo(""); }} data-testid={`btn-credit-${s.id}`}>Credit</Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Withdrawals */}
      <div className="crown-card p-6" data-testid="admin-bb-withdrawals">
        <div className="overline mb-3">Withdrawal Requests</div>
        <Table>
          <TableHeader><TableRow>
            <TableHead>When</TableHead><TableHead>User</TableHead><TableHead>Amount</TableHead>
            <TableHead>To Wallet</TableHead><TableHead>Status</TableHead><TableHead>Actions</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {withdrawals.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-slate-400 py-8">No withdrawals</TableCell></TableRow>}
            {withdrawals.map((w) => (
              <TableRow key={w.id} data-testid={`bb-w-${w.id}`}>
                <TableCell className="text-xs">{new Date(w.created_at).toLocaleString()}</TableCell>
                <TableCell className="text-xs">{w.user_email}<div className="text-slate-500">{w.user_id}</div></TableCell>
                <TableCell className="tabular"><UsdtAmount value={w.amount} decimals={2} /></TableCell>
                <TableCell className="text-[10px] tabular break-all max-w-[160px]">{w.wallet_address}</TableCell>
                <TableCell><Chip status={w.status} /></TableCell>
                <TableCell className="text-xs space-y-1">
                  {(w.status === "pending" || w.status === "approved") && (
                    <div className="space-y-1">
                      {w.status === "pending" && (
                        <Button size="sm" onClick={() => decide(w.id, "approved")} className="bg-blue-600 text-white w-full" data-testid={`btn-approve-${w.id}`}>Approve</Button>
                      )}
                      <div className="flex gap-1">
                        <Input placeholder="tx hash (paid)" value={txInputs[w.id] || ""} onChange={(e) => setTxInputs({ ...txInputs, [w.id]: e.target.value })} className="text-[10px] h-8" data-testid={`in-tx-${w.id}`} />
                        <Button size="sm" onClick={() => decide(w.id, "paid", txInputs[w.id])} className="bg-emerald-600 text-white" data-testid={`btn-paid-${w.id}`}>Paid</Button>
                      </div>
                      <Button size="sm" variant="outline" onClick={() => decide(w.id, "rejected")} data-testid={`btn-reject-${w.id}`}>Reject</Button>
                    </div>
                  )}
                  {w.status === "paid" && <div className="tabular text-[10px] break-all">{w.tx_hash}</div>}
                  {w.status === "rejected" && <div className="text-[10px] text-slate-400">{w.reason || "—"}</div>}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Credit dialog */}
      <Dialog open={!!creditFor} onOpenChange={(v) => !v && setCreditFor(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Credit Boom Box balance</DialogTitle></DialogHeader>
          {creditFor && (
            <div className="space-y-3 text-sm">
              <div>User: <span className="font-medium">{creditFor.user_email}</span></div>
              <div>Current available: <span className="tabular">{formatUsdt(creditFor.balance?.available_balance || 0)}</span></div>
              <div>
                <Label>Amount (USDT)</Label>
                <Input type="number" min={0} step="0.01" value={creditAmount} onChange={(e) => setCreditAmount(e.target.value)} data-testid="in-credit-amount" />
              </div>
              <div>
                <Label>Memo (optional)</Label>
                <Input value={creditMemo} onChange={(e) => setCreditMemo(e.target.value)} data-testid="in-credit-memo" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreditFor(null)}>Cancel</Button>
            <Button onClick={doCredit} className="bg-[#556B2F] text-white" data-testid="btn-confirm-credit">Confirm Credit</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardShell>
  );
}

// =============================================================
//  ADMIN · Notifications
// =============================================================
export function AdminNotifications() {
  const [broadcasts, setBroadcasts] = useState([]);
  const [users, setUsers] = useState([]);
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [target, setTarget] = useState("all"); // "all" | "users"
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [userSearch, setUserSearch] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [b, u] = await Promise.all([
        api.get("/admin/notifications"),
        api.get("/admin/users"),
      ]);
      setBroadcasts(b.data || []);
      setUsers((u.data || []).filter((x) => x.role !== "admin"));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const doSend = async () => {
    if (!title.trim() || !message.trim()) return toast.error("Title and message are required");
    if (target === "users" && selectedUserIds.length === 0) return toast.error("Select at least one user");
    setBusy(true);
    try {
      const r = await api.post("/admin/notifications", {
        title: title.trim(),
        message: message.trim(),
        target,
        user_ids: target === "users" ? selectedUserIds : [],
      });
      toast.success(`Sent to ${r.data.delivered_count} user${r.data.delivered_count === 1 ? "" : "s"}`);
      setTitle(""); setMessage(""); setSelectedUserIds([]); setUserSearch("");
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const doDelete = async (bid) => {
    if (!window.confirm("Delete this notification for all recipients?")) return;
    try {
      await api.delete(`/admin/notifications/${bid}`);
      toast.success("Notification deleted");
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const filteredUsers = users.filter((u) => {
    const q = userSearch.trim().toLowerCase();
    if (!q) return true;
    return (u.email || "").toLowerCase().includes(q) ||
           (u.full_name || "").toLowerCase().includes(q) ||
           (u.id || "").toLowerCase().includes(q);
  });

  const toggleUser = (id) => {
    setSelectedUserIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };

  return (
    <DashboardShell isAdmin>
      <div className="mb-6">
        <div className="overline">Admin</div>
        <h1 className="font-serif text-4xl mt-2">Notifications</h1>
        <p className="text-slate-400 text-sm mt-1">Send announcements to all users, selected users, or a specific user.</p>
      </div>

      {/* Compose */}
      <div className="crown-card p-6 mb-6" data-testid="notif-compose">
        <div className="overline mb-3">New notification</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-3">
            <div>
              <Label>Title</Label>
              <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Crown Coupon Company Update" maxLength={200} data-testid="notif-title" />
            </div>
            <div>
              <Label>Message</Label>
              <Textarea rows={5} value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Write your announcement here…" maxLength={4000} data-testid="notif-message" />
              <div className="text-[10px] text-slate-400 mt-1">{message.length} / 4000</div>
            </div>
          </div>
          <div className="space-y-3">
            <div>
              <Label>Recipients</Label>
              <div className="flex gap-2 mt-1">
                <button type="button" onClick={() => setTarget("all")} data-testid="notif-target-all" className={`px-3 py-1.5 rounded-md text-sm ${target === "all" ? "bg-[#556B2F] text-white" : "border border-white/10 text-slate-300"}`}>All users</button>
                <button type="button" onClick={() => setTarget("users")} data-testid="notif-target-users" className={`px-3 py-1.5 rounded-md text-sm ${target === "users" ? "bg-[#556B2F] text-white" : "border border-white/10 text-slate-300"}`}>Selected users</button>
              </div>
            </div>
            {target === "users" && (
              <div>
                <Input value={userSearch} onChange={(e) => setUserSearch(e.target.value)} placeholder="Search users by name or email…" data-testid="notif-user-search" />
                <div className="mt-2 max-h-64 overflow-y-auto rounded-md border border-white/10 divide-y divide-white/5">
                  {filteredUsers.length === 0 && <div className="p-3 text-xs text-slate-400">No users match.</div>}
                  {filteredUsers.slice(0, 200).map((u) => (
                    <label key={u.id} className="flex items-center gap-2 p-2 text-xs cursor-pointer hover:bg-white/[0.03]" data-testid={`notif-user-${u.id}`}>
                      <input type="checkbox" checked={selectedUserIds.includes(u.id)} onChange={() => toggleUser(u.id)} />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium truncate">{u.full_name || u.email}</div>
                        <div className="text-slate-400 truncate">{u.email}</div>
                      </div>
                    </label>
                  ))}
                </div>
                <div className="text-[10px] text-slate-400 mt-1">{selectedUserIds.length} selected</div>
              </div>
            )}
          </div>
        </div>
        <div className="mt-5 flex justify-end">
          <Button onClick={doSend} disabled={busy} className="bg-[#556B2F] text-white" data-testid="btn-send-notif">
            {busy ? "Sending…" : "Send notification"}
          </Button>
        </div>
      </div>

      {/* History */}
      <div className="crown-card p-6" data-testid="notif-history">
        <div className="flex items-center justify-between mb-3">
          <div className="overline">Sent notifications</div>
          <Button variant="outline" size="sm" onClick={load}>Refresh</Button>
        </div>
        <Table>
          <TableHeader><TableRow>
            <TableHead>When</TableHead><TableHead>Title</TableHead><TableHead>Target</TableHead>
            <TableHead>Delivered</TableHead><TableHead>Unread</TableHead><TableHead>Actions</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {broadcasts.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-slate-400 py-8">No notifications sent yet</TableCell></TableRow>}
            {broadcasts.map((b) => (
              <TableRow key={b.id} data-testid={`bc-${b.id}`}>
                <TableCell className="text-xs">{new Date(b.sent_at).toLocaleString()}</TableCell>
                <TableCell><div className="font-medium">{b.title}</div><div className="text-xs text-slate-400 truncate max-w-[320px]">{b.message}</div></TableCell>
                <TableCell className="text-xs">{b.target === "all" ? "All users" : `${(b.target_user_ids || []).length} user(s)`}</TableCell>
                <TableCell className="tabular text-xs">{b.delivered_count}</TableCell>
                <TableCell className="tabular text-xs">{b.unread_count ?? "—"}</TableCell>
                <TableCell>
                  <Button size="sm" variant="outline" onClick={() => doDelete(b.id)} data-testid={`btn-del-${b.id}`}>Delete</Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </DashboardShell>
  );
}


// =============================================================
//  ADMIN · MLM Network
// =============================================================
// Search any user by id/name/email/referral code, then view their full 3-level
// downline tree. Reads directly from the existing users.referred_by field —
// no separate MLM tables.
export function AdminMLM() {
  const [q, setQ] = useState("");
  const [candidates, setCandidates] = useState([]);
  const [teamData, setTeamData] = useState(null);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);

  const search = async () => {
    if (!q.trim()) return;
    try {
      const { data } = await api.get(`/admin/users?q=${encodeURIComponent(q)}`);
      setCandidates(data.filter((x) => x.role !== "admin"));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const pick = async (u) => {
    setSelected(u); setTeamData(null); setLoading(true);
    try {
      const { data } = await api.get(`/admin/users/${u.id}/team`);
      setTeamData(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  return (
    <DashboardShell isAdmin>
      <div className="mb-6">
        <div className="overline">Admin</div>
        <h1 className="font-serif text-4xl mt-2">MLM Network</h1>
        <p className="text-slate-400 text-sm mt-1">Search a user by ID, name, email or referral code and view their complete referral tree.</p>
      </div>

      <div className="crown-card p-6 mb-6">
        <div className="flex gap-2">
          <Input placeholder="Search by user ID, name, email or referral code…" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && search()} data-testid="mlm-search" />
          <Button onClick={search} className="bg-[#556B2F] text-white" data-testid="mlm-search-btn">Search</Button>
        </div>

        {candidates.length > 0 && (
          <div className="mt-4 rounded-md border border-white/10 divide-y divide-white/5" data-testid="mlm-candidates">
            {candidates.slice(0, 12).map((c) => (
              <button
                key={c.id}
                onClick={() => pick(c)}
                data-testid={`mlm-pick-${c.id}`}
                className={`w-full text-left p-3 text-xs hover:bg-white/[0.03] transition ${selected?.id === c.id ? "bg-[#D4AF37]/10" : ""}`}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-medium">{c.full_name || c.email}</div>
                    <div className="text-slate-400">{c.email} · {c.id} · Ref {c.referral_id}</div>
                  </div>
                  <div className="text-right">
                    <div className="tabular">{c.direct_referrals_count ?? 0} direct</div>
                    <Chip status={c.status} />
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {selected && (
        <div className="crown-card p-6" data-testid="mlm-tree">
          <div className="flex items-baseline justify-between mb-3">
            <div>
              <div className="overline">Root</div>
              <div className="font-medium">{selected.full_name || selected.email}</div>
              <div className="text-xs text-slate-400">{selected.email} · {selected.id} · Ref {selected.referral_id}</div>
            </div>
          </div>
          {loading && <div className="text-sm text-slate-400 py-6">Loading team…</div>}
          {teamData && <TeamView data={teamData} />}
        </div>
      )}
    </DashboardShell>
  );
}

